/**
 * permissionsService — read wrapper for the WS-ENT-3 viewer-scoped
 * permissions endpoint (BE 46116f1c).
 *
 * Why a NEW file (not folded into userService.ts):
 *   - Separation of concerns. Permissions are the field-policy SSOT;
 *     userService owns the profile/auth user shape. Folding the
 *     permission catalogue into the user payload would advertise a
 *     "user owns its own permission set" model that the BE rejected —
 *     the BE deliberately published a separate endpoint with its own
 *     cache window so the FE could refetch policies WITHOUT churning
 *     the user object.
 *   - Lint allowlist: `*service.ts` is whitelisted in eslint.config.js
 *     for direct `apiClient` import.
 *
 * Wire contract:
 *   GET /api/me/permissions/ → { permissions, role, cache_max_age }
 *
 * Parse posture mirrors workspaceService / mentorshipService — every
 * response routes through `parseAtBoundary` so a backend serializer
 * drift surfaces as a typed `ResponseShapeError` (RetryBanner's
 * shape-specific branch) instead of an `undefined` crash three
 * components downstream. captureError SSOT for the failure telemetry.
 */

import { apiClient } from '../apiClient';
import { API } from '../apiUrls';
import { captureError } from '../core/errors/captureError';
import {
    PermissionsSchema,
    type PermissionsPayload,
} from '../schemas/permissions';
import { ResponseShapeError } from '../schemas/types';
import type { ZodError, ZodTypeAny } from 'zod';

/** Local boundary helper — same shape as workspaceService's so the
 *  failure telemetry payload is structurally identical across slices.
 *  Kept LOCAL (not exported) so call-sites in this module can't
 *  re-invent the captureError/throw shape; the ts-morph sweep
 *  (ADR-0002 §4) will collapse these into a shared helper later. */
function parseAtBoundary<S extends ZodTypeAny>(
    schema: S,
    raw: unknown,
    ctx: { op: string },
): ReturnType<S['parse']> {
    const result = schema.safeParse(raw);
    if (!result.success) {
        const zodError: ZodError = result.error;
        captureError(zodError, {
            scope: 'permissionsService',
            op: ctx.op,
            extra: { issues: zodError.issues },
        });
        throw new ResponseShapeError(ctx.op, zodError);
    }
    return result.data as ReturnType<S['parse']>;
}

/**
 * Read the viewer's permission catalogue.
 *
 * Returns the raw wire shape (string array of permission keys + role +
 * cache hint). The PermissionsProvider converts the array to a
 * `Set<string>` for O(1) hasPermission lookups; we keep the array at
 * the service boundary because the wire schema IS an array.
 *
 * @param signal Optional AbortSignal — wire from useAsyncResource so
 *               unmount/dep-change actually cancels the request.
 */
export async function getMePermissions(
    opts: { signal?: AbortSignal } = {},
): Promise<PermissionsPayload> {
    const response = await apiClient.get(
        API.me.permissions,
        opts.signal ? { signal: opts.signal } : undefined,
    );
    return parseAtBoundary(PermissionsSchema, response.data, {
        op: 'getMePermissions',
    });
}
